import { createTheme } from '@mui/material/styles';

// Color palette based on current usage
const colors = {
  // Primary colors (used in buttons, links, etc.)
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  // Secondary colors - Aspiration Orange for action buttons
  secondary: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#ffffff',
  },
  // Success colors
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff',
  },
  // Error colors
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  // Warning colors
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff',
  },
  // Info colors
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff',
  },
  // Background colors - Modern subtle gradient
  background: {
    default: '#f5f7fa', // Softer, more modern gray
    paper: '#ffffff',
    subtle: '#e4e8ec', // Card headers: visible band vs paper
  },
  // Table colors (header styling is neutral via MuiTableCell / StyledTableCell)
  table: {
    headerBg: '#f5f5f5',
    headerText: 'rgba(0, 0, 0, 0.87)',
    rowHover: 'rgba(0, 0, 0, 0.04)',
    rowOdd: 'rgba(0, 0, 0, 0.02)',
    border: 'rgba(224, 224, 224, 1)',
  },
  // Text colors
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  // Scrollbar colors
  scrollbar: {
    track: '#f1f1f1',
    thumb: '#888',
    thumbHover: '#555',
  },
};

// Shape/border radius values
const shapes = {
  // Button shapes
  button: {
    small: '4px',
    medium: '8px',
    large: '30px', // Rounded buttons
    pill: '50px', // Fully rounded
  },
  // Card shapes - More modern rounded corners
  card: {
    small: '8px',
    medium: '12px',
    large: '16px',
    default: '12px', // More modern default
  },
  // Input/TextField shapes - More rounded
  input: {
    small: '8px',
    medium: '10px',
    large: '12px',
  },
  // Table shapes
  table: {
    borderRadius: '4px',
  },
};

// Typography settings
const typography = {
  fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2rem',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
  },
  button: {
    fontWeight: 500,
    textTransform: 'none', // Override default uppercase
  },
};

// Create the MUI theme
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    background: colors.background,
    text: colors.text,
  },
  typography,
  shape: {
    borderRadius: 12, // More modern default border radius
  },
  components: {
    // Button component overrides
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: shapes.button.medium,
          fontWeight: typography.button.fontWeight,
          textTransform: typography.button.textTransform,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        // Primary button with gradient (for special buttons like "Start Test")
        containedPrimary: {
          background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
          '&:hover': {
            background: `linear-gradient(90deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
          },
        },
        // Large rounded buttons
        sizeLarge: {
          borderRadius: shapes.button.large,
          padding: '12px 32px',
          fontSize: '1rem',
        },
      },
      variants: [
        {
          props: { variant: 'contained', size: 'large' },
          style: {
            borderRadius: shapes.button.large,
            padding: '12px 32px',
            fontWeight: 'bold',
          },
        },
        {
          props: { variant: 'rounded' },
          style: {
            borderRadius: shapes.button.large,
          },
        },
        {
          props: { variant: 'pill' },
          style: {
            borderRadius: shapes.button.pill,
          },
        },
      ],
    },
    // Card component overrides - Modern shadows and hover effects
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: shapes.card.default,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.subtle,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiCardHeader-title': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    // TextField component overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: shapes.input.medium,
          },
        },
      },
    },
    // Select component overrides - Modern styling
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiSelect-select': {
            padding: '14px 40px 14px 14px',
            textAlign: 'center',
            minHeight: '20px',
            lineHeight: '20px',
          },
          '& .MuiSelect-icon': {
            right: '12px',
            color: 'rgba(0, 0, 0, 0.87)'
          }
        }
      }
    },
    // MenuItem component overrides - For Select dropdowns (only apply when inside Menu)
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-selected': {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.main',
            }
          }
        }
      }
    },
    // FormControl component overrides
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: colors.background.paper,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
            '&.Mui-focused': {
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
            },
            '& .MuiSelect-select': {
              borderRadius: 8,
            }
          }
        }
      }
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    // Table component overrides
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          fontWeight: 600,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        body: {
          fontSize: '14px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: colors.table.rowOdd,
          },
          '&:hover': {
            backgroundColor: colors.table.rowHover,
          },
          '&:last-child td, &:last-child th': {
            border: 0,
          },
        },
      },
    },
    // Paper component overrides - Modern styling
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: shapes.card.default,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        elevation4: {
          boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
        },
      },
    },
    // Chip component overrides
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: shapes.button.small,
        },
      },
    },
  },
});

// Export colors and shapes for direct use in components
export { colors, shapes };

// Export theme object
export default theme;
